import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CreateTaskModal({ listId, task, onClose, onCreated }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(task?.title || '');
  const [priority, setPriority] = useState(task?.priority || 5);
  const [points, setPoints] = useState(task?.points || 5);
  const [dueDate, setDueDate] = useState(task?.due_date || '');
  const [tags, setTags] = useState(task?.tags?.join(', ') || '');
  const [recurrent, setRecurrent] = useState(task?.recurrent || false);
  const [recurrenceDays, setRecurrenceDays] = useState(task?.recurrence_days || 1);
  const [recurrencePreset, setRecurrencePreset] = useState('daily');
  const [loading, setLoading] = useState(false);

  // Preset options for recurrence
  const presets = [
    { key: 'daily', days: 1 },
    { key: 'weekly', days: 7 },
    { key: 'biweekly', days: 14 },
    { key: 'monthly', days: 30 },
    { key: 'custom', days: null }
  ];

  const handlePresetChange = (preset) => {
    setRecurrencePreset(preset.key);
    if (preset.days) {
      setRecurrenceDays(preset.days);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    const tagsArray = tags.split(',').map(t => t.trim()).filter(t => t);

    setLoading(true);
    try {
      const taskData = {
        title,
        priority,
        points,
        due_date: dueDate || null,
        tags: tagsArray,
        recurrent,
        recurrence_days: recurrent ? recurrenceDays : null
      };

      if (task) {
        await axios.put(
          `${API}/tasks/${task.task_id}`,
          taskData,
          { withCredentials: true }
        );
        toast.success(t('messages.taskUpdated'));
      } else {
        await axios.post(
          `${API}/tasks`,
          { list_id: listId, ...taskData },
          { withCredentials: true }
        );
        toast.success(t('messages.taskCreated'));
      }
      onCreated();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error(t('messages.errorSavingTask'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50" onClick={onClose}>
      <div
        className="bg-white dark:bg-zinc-800 border-t-2 sm:border-2 border-[#18181B] dark:border-zinc-600 rounded-t-2xl sm:rounded-2xl hard-shadow p-4 sm:p-6 w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="create-task-modal"
      >
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-white">
            {task ? t('modals.editTask') : t('modals.newTask')}
          </h2>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 sm:p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5 text-zinc-900 dark:text-white" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-bold mb-1.5 sm:mb-2 text-zinc-900 dark:text-white">{t('modals.taskTitle')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('modals.taskTitlePlaceholder')}
              className="w-full h-10 sm:h-12 rounded-lg sm:rounded-xl border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 sm:px-4 text-sm sm:text-base text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-shadow"
              data-testid="task-title-input"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-bold mb-1.5 sm:mb-2 text-zinc-900 dark:text-white">
              {t('modals.priority')} <span className="font-mono text-[#FF8BA7]">{priority}</span>
            </label>
            <input
              type="range"
              min="1"
              max="10"
              value={priority}
              onChange={(e) => setPriority(parseInt(e.target.value))}
              className="w-full h-2 bg-zinc-200 dark:bg-zinc-600 rounded-lg appearance-none cursor-pointer"
              data-testid="priority-slider"
            />
            <div className="flex justify-between text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400 mt-1">
              <span>{t('modals.priorityLow')}</span>
              <span>{t('modals.priorityHigh')}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-bold mb-1.5 sm:mb-2 text-zinc-900 dark:text-white">
              {t('modals.rewardPoints')} <span className="font-mono text-[#FFC247]">{points}</span>
            </label>
            <input
              type="range"
              min="1"
              max="10"
              value={points}
              onChange={(e) => setPoints(parseInt(e.target.value))}
              className="w-full h-2 bg-zinc-200 dark:bg-zinc-600 rounded-lg appearance-none cursor-pointer"
              data-testid="points-slider"
            />
            <div className="flex justify-between text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400 mt-1">
              <span>1 {t('modals.point')}</span>
              <span>10 {t('modals.points')}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-bold mb-1.5 sm:mb-2 text-zinc-900 dark:text-white">{t('modals.dueDate')}</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full h-10 sm:h-12 rounded-lg sm:rounded-xl border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 sm:px-4 text-sm sm:text-base text-zinc-900 dark:text-white focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-shadow"
              data-testid="due-date-input"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-bold mb-1.5 sm:mb-2 text-zinc-900 dark:text-white">{t('modals.tags')}</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder={t('modals.tagsPlaceholder')}
              className="w-full h-10 sm:h-12 rounded-lg sm:rounded-xl border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 sm:px-4 text-sm sm:text-base text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-shadow"
              data-testid="tags-input"
            />
          </div>

          {/* Recurrent Task Section */}
          <div className={`p-3 sm:p-4 border-2 border-[#18181B] dark:border-zinc-600 rounded-lg sm:rounded-xl transition-colors ${recurrent ? 'bg-[#ADE498]/20 dark:bg-[#ADE498]/10' : 'bg-zinc-50 dark:bg-zinc-700'}`}>
            <div className="flex items-center gap-2 sm:gap-3">
              <input
                type="checkbox"
                id="recurrent"
                checked={recurrent}
                onChange={(e) => setRecurrent(e.target.checked)}
                className="w-4 h-4 sm:w-5 sm:h-5 rounded border-2 border-[#18181B] dark:border-zinc-500 cursor-pointer flex-shrink-0"
                data-testid="recurrent-checkbox"
              />
              <label htmlFor="recurrent" className="flex-1 cursor-pointer">
                <div className="font-bold text-xs sm:text-sm flex items-center gap-2 text-zinc-900 dark:text-white">
                  <RefreshCw className="w-4 h-4" />
                  {t('modals.recurrentTask')}
                </div>
                <div className="text-[10px] sm:text-xs text-zinc-600 dark:text-zinc-400">{t('modals.recurrentDescription')}</div>
              </label>
            </div>

            {/* Recurrence Options - Only shown when recurrent is checked */}
            {recurrent && (
              <div className="mt-4 pt-4 border-t border-[#18181B]/20 dark:border-zinc-600 space-y-3">
                <label className="block text-xs sm:text-sm font-bold text-zinc-900 dark:text-white">{t('modals.recurrenceInterval')}</label>
                
                {/* Preset buttons */}
                <div className="flex flex-wrap gap-2">
                  {presets.map((preset) => (
                    <button
                      key={preset.key}
                      type="button"
                      onClick={() => handlePresetChange(preset)}
                      className={`px-3 py-1.5 text-xs font-bold rounded-full border-2 border-[#18181B] dark:border-zinc-600 transition-all ${
                        recurrencePreset === preset.key
                          ? 'bg-[#18181B] text-white'
                          : 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-600'
                      }`}
                      data-testid={`recurrence-preset-${preset.key}`}
                    >
                      {t(`modals.recurrencePresets.${preset.key}`)}
                    </button>
                  ))}
                </div>

                {/* Custom days input - Only shown when custom is selected */}
                {recurrencePreset === 'custom' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={recurrenceDays}
                      onChange={(e) => setRecurrenceDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))}
                      className="w-20 h-10 rounded-lg border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 text-sm text-center text-zinc-900 dark:text-white focus:outline-none focus:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] transition-shadow"
                      data-testid="recurrence-days-input"
                    />
                    <span className="text-sm font-medium text-zinc-900 dark:text-white">{t('modals.recurrenceDays')}</span>
                  </div>
                )}

                {/* Preview of next occurrence */}
                <p className="text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-600 rounded-lg px-3 py-2">
                  📅 {recurrenceDays === 1 
                    ? (t('modals.recurrencePresets.daily').split(' ')[0])
                    : `Cada ${recurrenceDays} días`
                  }
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-2 sm:gap-3 pt-2 sm:pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-[#18181B] dark:text-white font-bold py-2.5 sm:py-3 px-4 sm:px-6 text-sm sm:text-base hover:bg-zinc-100 dark:hover:bg-zinc-600 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all"
              data-testid="cancel-btn"
            >
              {t('modals.cancel')}
            </button>
            <button
              type="submit"
              disabled={!title.trim() || loading}
              className="flex-1 rounded-full border-2 border-[#18181B] bg-[#18181B] text-white font-bold py-2.5 sm:py-3 px-4 sm:px-6 text-sm sm:text-base hover:bg-[#333] hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="save-btn"
            >
              {loading ? t('modals.saving') : task ? t('modals.update') : t('modals.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
